/**
 * OwnerUserAdminModal — owner-only "Manage account" panel on the Athlete Dossier.
 *
 * Three modes:
 *  • "edit"     — edit any user field (name, email, phone, status, person
 *                 type, sport, address, emergency contact)
 *  • "role"     — promote (client → coach) or demote (coach → client)
 *  • "delete"   — hard-delete the user + cascade everything keyed to them
 *
 * Owner-only — caller must gate `user?.role === "owner"`. Server enforces
 * RBAC again so this is purely a UX gate, but mounting it for non-owners
 * is wasted DOM.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    X, Loader2, Save, Shield, UserMinus, UserPlus, Trash2, AlertTriangle, ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_STATUSES = [
    { value: "lead", label: "Lead" },
    { value: "prospect", label: "Prospect (scheduled to join)" },
    { value: "assessed", label: "Assessed (came in for assessment)" },
    { value: "member", label: "Member" },
    { value: "non_member", label: "Non-Member" },
    { value: "inactive", label: "Inactive" },
    { value: "mnp", label: "MNP (pending approval)" },
];

const PERSON_TYPES = [
    { value: "", label: "—" },
    { value: "parent", label: "Parent (manages a child)" },
    { value: "child", label: "Child (athlete)" },
    { value: "adult_client", label: "Adult client (trains themselves)" },
];

export default function OwnerUserAdminModal({ user, onClose, onChanged }) {
    const [tab, setTab] = useState("edit"); // edit | role | delete
    const [saving, setSaving] = useState(false);

    if (!user) return null;
    const isCoach = user.role === "coach";
    const isOwner = user.role === "owner";

    return (
        <div
            data-testid="owner-user-admin-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
            >
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Owner controls</div>
                        <h3 className="font-display text-2xl tracking-wider uppercase mt-0.5">
                            Manage {user.name || user.email}
                        </h3>
                        <div className="mt-1 text-[11px] text-zinc-400">
                            {user.email} · role: <span className="text-white">{user.role || "client"}</span>
                            {isOwner && <span className="ml-2 text-amber-400">[ Owner — cannot be modified ]</span>}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="owner-admin-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="border-b border-zinc-800 flex gap-1 px-4 shrink-0">
                    {[
                        { key: "edit", label: "Edit profile", icon: Save },
                        { key: "role", label: isCoach ? "Demote to client" : "Promote to coach", icon: isCoach ? UserMinus : UserPlus },
                        { key: "delete", label: "Delete user", icon: Trash2 },
                    ].map((t) => {
                        const Icon = t.icon;
                        const on = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                data-testid={`owner-admin-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                disabled={isOwner && t.key !== "edit"}
                                className={`inline-flex items-center gap-2 px-3 py-3 text-[11px] tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap ${
                                    on ? "border-brand-red text-white" : "border-transparent text-zinc-300 hover:text-white"
                                } ${isOwner && t.key !== "edit" ? "opacity-40 cursor-not-allowed" : ""}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {tab === "edit" && (
                        <EditPanel user={user} saving={saving} setSaving={setSaving} onChanged={onChanged} />
                    )}
                    {tab === "role" && !isOwner && (
                        <RolePanel user={user} saving={saving} setSaving={setSaving} onChanged={onChanged} onClose={onClose} />
                    )}
                    {tab === "delete" && !isOwner && (
                        <DeletePanel user={user} saving={saving} setSaving={setSaving} onChanged={onChanged} onClose={onClose} />
                    )}
                </div>
            </div>
        </div>
    );
}


// ---------------- Edit panel ----------------

function EditPanel({ user, saving, setSaving, onChanged }) {
    const [f, setF] = useState({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        status: user.status || "lead",
        person_type: user.person_type || "",
        primary_sport: user.primary_sport || "",
        parent_first_name: user.parent_first_name || "",
        parent_last_name: user.parent_last_name || "",
        internal_note: user.internal_note || "",
        // Address subdoc
        addr_street: user.address?.street || "",
        addr_city: user.address?.city || "",
        addr_state: user.address?.state || "",
        addr_zip: user.address?.zip || "",
        // Emergency contact subdoc
        ec_name: user.emergency_contact?.name || "",
        ec_relation: user.emergency_contact?.relationship || "",
        ec_phone: user.emergency_contact?.phone || "",
    });
    const [showAdvanced, setShowAdvanced] = useState(false);
    const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

    const emailChanged = f.email.trim().toLowerCase() !== (user.email || "").toLowerCase();

    const submit = async (e) => {
        e?.preventDefault?.();
        if (emailChanged) {
            const ok = window.confirm(
                "Changing this user's email will break any magic-link emails they were already sent. " +
                "Existing logins keep working until they sign out. Continue?",
            );
            if (!ok) return;
        }
        setSaving(true);
        try {
            // Trim every input. Empty strings are deliberate — backend treats
            // them as "clear this field" so the coach can wipe stuck legacy
            // values (e.g. an old email-prefix accidentally stored as
            // parent_first_name).
            const trim = (s) => (s ?? "").trim();

            // Auto-sync Display Name → Parent first/last (UX fix 2026-02-23):
            // If the coach changed Display Name but didn't separately edit
            // Parent first/last, split the display name on the first space
            // and overwrite parent_first/last so the roster header (which
            // renders from parent_first + parent_last) reflects the rename
            // immediately. If parent_first/last WERE explicitly edited, we
            // respect the explicit values and leave them alone.
            const nameTouched = trim(f.name) !== trim(user.name || "");
            const parentFirstTouched = trim(f.parent_first_name) !== trim(user.parent_first_name || "");
            const parentLastTouched  = trim(f.parent_last_name)  !== trim(user.parent_last_name  || "");
            let resolvedFirst = trim(f.parent_first_name);
            let resolvedLast  = trim(f.parent_last_name);
            if (nameTouched && !parentFirstTouched && !parentLastTouched && trim(f.name)) {
                const parts = trim(f.name).split(/\s+/);
                resolvedFirst = parts[0] || "";
                resolvedLast  = parts.slice(1).join(" ") || "";
            }

            const payload = {
                name: trim(f.name),
                email: trim(f.email).toLowerCase(),
                phone: trim(f.phone),
                status: f.status,
                person_type: f.person_type || "",
                primary_sport: f.primary_sport || "",
                parent_first_name: resolvedFirst,
                parent_last_name: resolvedLast,
                internal_note: trim(f.internal_note),
                address: (f.addr_street || f.addr_city || f.addr_state || f.addr_zip) ? {
                    street: trim(f.addr_street) || null,
                    city: trim(f.addr_city) || null,
                    state: trim(f.addr_state) || null,
                    zip: trim(f.addr_zip) || null,
                } : null,
                emergency_contact: (f.ec_name || f.ec_phone) ? {
                    name: trim(f.ec_name) || null,
                    relationship: trim(f.ec_relation) || null,
                    phone: trim(f.ec_phone) || null,
                } : null,
            };
            // Drop only undefined / nullish-and-untouched values. Keep empty
            // strings — those are intentional clears handled by the backend.
            const cleaned = Object.fromEntries(
                Object.entries(payload).filter(([, v]) => v !== null && v !== undefined),
            );
            // Email is required — never send empty.
            if (cleaned.email === "") delete cleaned.email;
            const r = await axios.patch(
                `${API}/coach/users/${user.user_id}`,
                cleaned,
                { withCredentials: true },
            );
            toast.success("Saved");
            onChanged?.(r.data?.user);
        } catch (err) {
            toast.error("Couldn't save", { description: err?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                    label="Display name"
                    hint="Used on the roster, emails, and dashboards. Parent first/last below will auto-fill from this on save (unless you edit them too)."
                >
                    <input data-testid="edit-name" value={f.name} onChange={set("name")} className={inp} />
                </Field>
                <Field label="Email" hint={emailChanged ? "⚠ Will require they use the new email going forward." : null}>
                    <input data-testid="edit-email" type="email" value={f.email} onChange={set("email")} className={inp} />
                </Field>
                <Field label="Phone">
                    <input data-testid="edit-phone" value={f.phone} onChange={set("phone")} className={inp} />
                </Field>
                <Field label="Status">
                    <select data-testid="edit-status" value={f.status} onChange={set("status")} className={inp}>
                        {CLIENT_STATUSES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                    </select>
                </Field>
                <Field label="Parent first name" hint="Used to personalize emails (e.g. 'Hi Mike,'). Leave blank to fall back to Display name.">
                    <input data-testid="edit-parent-first-name" value={f.parent_first_name} onChange={set("parent_first_name")} className={inp} placeholder="e.g. Mike" />
                </Field>
                <Field label="Parent last name">
                    <input data-testid="edit-parent-last-name" value={f.parent_last_name} onChange={set("parent_last_name")} className={inp} placeholder="e.g. Krohn" />
                </Field>
                <Field label="Person type" hint="Use 'Adult client' if they train themselves (no kid)">
                    <select data-testid="edit-person-type" value={f.person_type} onChange={set("person_type")} className={inp}>
                        {PERSON_TYPES.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                    </select>
                </Field>
                <Field label="Primary sport">
                    <input data-testid="edit-primary-sport" value={f.primary_sport} onChange={set("primary_sport")} className={inp} placeholder="e.g. baseball" />
                </Field>
            </div>

            <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-white inline-flex items-center gap-1"
            >
                <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                {showAdvanced ? "Hide" : "Show"} address + emergency contact
            </button>

            {showAdvanced && (
                <div className="space-y-3 pt-2 border-t border-zinc-800">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mt-3">Address</div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div className="sm:col-span-2"><Field label="Street"><input data-testid="edit-addr-street" value={f.addr_street} onChange={set("addr_street")} className={inp} /></Field></div>
                        <Field label="City"><input data-testid="edit-addr-city" value={f.addr_city} onChange={set("addr_city")} className={inp} /></Field>
                        <Field label="State"><input data-testid="edit-addr-state" value={f.addr_state} onChange={set("addr_state")} className={inp} /></Field>
                        <Field label="Zip"><input data-testid="edit-addr-zip" value={f.addr_zip} onChange={set("addr_zip")} className={inp} /></Field>
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mt-3">Emergency contact</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Name"><input data-testid="edit-ec-name" value={f.ec_name} onChange={set("ec_name")} className={inp} /></Field>
                        <Field label="Relationship"><input data-testid="edit-ec-relation" value={f.ec_relation} onChange={set("ec_relation")} className={inp} /></Field>
                        <Field label="Phone"><input data-testid="edit-ec-phone" value={f.ec_phone} onChange={set("ec_phone")} className={inp} /></Field>
                    </div>
                </div>
            )}

            <div className="border-t border-zinc-800 pt-4 flex justify-end gap-2">
                <button
                    type="submit"
                    data-testid="owner-edit-save"
                    disabled={saving}
                    className="btn-tactical text-xs"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save changes
                </button>
            </div>
        </form>
    );
}


// ---------------- Role panel ----------------

function RolePanel({ user, saving, setSaving, onChanged, onClose }) {
    const isCoach = user.role === "coach";

    const promote = async () => {
        setSaving(true);
        try {
            await axios.post(
                `${API}/coach/clients/${user.user_id}/promote-to-coach`,
                {},
                { withCredentials: true },
            );
            toast.success("Promoted to coach", {
                description: "They now have access to the coach console with default permissions.",
            });
            onChanged?.();
            onClose?.();
        } catch (e) {
            toast.error("Couldn't promote", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const demote = async () => {
        const ok = window.confirm(
            `Demote ${user.name || user.email} back to client? They'll lose coach access immediately and be signed out.`,
        );
        if (!ok) return;
        setSaving(true);
        try {
            await axios.delete(
                `${API}/coach/coaches/${user.user_id}`,
                { withCredentials: true },
            );
            toast.success("Demoted to client");
            onChanged?.();
            onClose?.();
        } catch (e) {
            toast.error("Couldn't demote", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 space-y-5">
            {!isCoach ? (
                <>
                    <div className="border border-emerald-700/40 bg-emerald-900/10 rounded-xl p-5">
                        <div className="inline-flex items-center gap-2 text-emerald-300 mb-2">
                            <Shield className="w-4 h-4" />
                            <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Promote to coach</span>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            Gives <b>{user.name || user.email}</b> access to the coach console with default permissions
                            (run assessments, edit notes, change client status, manage announcements). You can fine-tune
                            their permissions from Admin → Coaches afterwards.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="role-promote-confirm"
                        onClick={promote}
                        disabled={saving}
                        className="btn-tactical text-xs"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                        Promote to coach
                    </button>
                </>
            ) : (
                <>
                    <div className="border border-amber-700/40 bg-amber-900/10 rounded-xl p-5">
                        <div className="inline-flex items-center gap-2 text-amber-300 mb-2">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Demote to client</span>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            Revokes coach access for <b>{user.name || user.email}</b>. They keep their user account
                            (history, assignments, billing) but lose console + permissions. Existing sessions are killed
                            so the revoke takes effect immediately.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="role-demote-confirm"
                        onClick={demote}
                        disabled={saving}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-amber-600 bg-amber-700/20 text-amber-200 hover:bg-amber-700/30 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                        Demote to client
                    </button>
                </>
            )}
        </div>
    );
}


// ---------------- Delete panel ----------------

function DeletePanel({ user, saving, setSaving, onChanged, onClose }) {
    const [confirmText, setConfirmText] = useState("");
    const expected = (user.email || "").toLowerCase();
    const canDelete = confirmText.trim().toLowerCase() === expected;

    const handleDelete = async () => {
        setSaving(true);
        try {
            const r = await axios.delete(
                `${API}/coach/users/${user.user_id}`,
                { withCredentials: true },
            );
            const counts = r.data?.cascade || {};
            const summary = Object.entries(counts).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.split(":")[0]}`).join(", ");
            toast.success("User deleted", { description: summary || undefined });
            onChanged?.({ deleted: true });
            onClose?.();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 space-y-5">
            <div className="border border-brand-red bg-brand-red/5 rounded-xl p-5">
                <div className="inline-flex items-center gap-2 text-brand-red mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Hard delete</span>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">
                    Permanently deletes <b>{user.name || user.email}</b> and cascades:
                </p>
                <ul className="text-[12px] text-zinc-400 mt-2 space-y-0.5 list-disc list-inside">
                    <li>All sessions, magic-link tokens, program assignments + progress</li>
                    <li>Any athletes under their account (kids), packs, subscriptions, invites</li>
                </ul>
                <p className="text-sm text-zinc-300 mt-3">
                    Their <b>workout history, billing invoices, and audit logs</b> are preserved by Stripe + audit collections.
                    This is <b>not reversible</b>.
                </p>
            </div>
            <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">
                    Type the user's email (<span className="text-white">{expected}</span>) to confirm:
                </label>
                <input
                    data-testid="delete-confirm-input"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className={inp}
                />
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-4">
                <button type="button" onClick={onClose} disabled={saving} className="btn-ghost-tactical text-xs">
                    Cancel
                </button>
                <button
                    type="button"
                    data-testid="delete-user-confirm"
                    onClick={handleDelete}
                    disabled={!canDelete || saving}
                    className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Delete forever
                </button>
            </div>
        </div>
    );
}


// ---------------- Form helpers ----------------

const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red";

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 block mb-1">{label}</span>
            {children}
            {hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}
        </label>
    );
}
